import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IBooking } from 'shared-types/SharedTypes';
import { ICancelConfirmState } from './types';
import { IDiarySchedule } from 'shared-types/DiaryTypes';
import { DiaryRootState } from 'appRoot/rootReducer';
import DateUtilsService from 'external-services/date-utils-service';
import BookingLookupService from 'external-services/booking-lookup-service';
import { orderBy } from 'lodash-es';
import { cloneDeep, isEmpty } from 'lodash-es';
import CompareObjectsService from 'external-services/compare-objects-service/CompareObjects';
import CompareObjects, {
  IChangeDetails,
} from 'external-services/compare-objects-service/CompareObjects';
import {BookingMode} from "booking-creator/reducers/display/types";

const NS = 'cancelConfirmSlice';

const initialState: ICancelConfirmState = {
  activeBookingToCompare: null,
  customerToCompare: null,
  isCancelConfirmStateLoading: true,
  isCancelConfirmStateSet: false,
  isAutoRemoveOverlay: false,
};

export const setCancelConfirm = createAsyncThunk(
  'cancelConfirm/setCancelConfirm',
  async (payload: void, { dispatch, getState }): Promise<any> => {
    const {
      displayReducer: { activeBooking },
      customerReducer: { customer },
    } = getState() as DiaryRootState;


    return {
      activeBooking: !!activeBooking ? cloneDeep(activeBooking) : null,
      customer: !!customer ? cloneDeep(customer) : null,
    };
  }
);

export const doesBookingFormHaveUnsavedData = createAsyncThunk(
  'cancelConfirm/doesBookingFormHaveUnsavedData',
  async (payload: void, { dispatch, getState }): Promise<any> => {
    const {
      displayReducer: { activeBooking, mode },
      customerReducer: { customer },
      cancelConfirmReducer: { activeBookingToCompare: _activeBooking, customerToCompare: _customer, },
    } = getState() as DiaryRootState;

    if (!_activeBooking || mode === BookingMode.viewBooking) {
      return false;
    }

    const getChangesAsText = (changes: IChangeDetails[]): string => {
      let errorContent = `PropertyName: ConflictedBookingDetail -> YourBookingDetail \n`;
      changes.forEach((change) => {
        errorContent += `${change.property}: ${change.originalValue} -> ${change.newValue} \n`;
      });

      return errorContent;
    };

    const bookingChanges: IChangeDetails[] = CompareObjectsService.computeObjectDifference(
      activeBooking,
      _activeBooking,
      '',
      true,
      ['preferredSectionId']
    );

    if (bookingChanges.length) {
      console.log(NS, 'booking changes detected', getChangesAsText(bookingChanges));
      return true;
    }

    const customerChanges: IChangeDetails[] = CompareObjectsService.computeObjectDifference(
      customer,
      _customer,
      '',
      true,
      []
    );

    // Compare customer details
    if (customerChanges.length) {
      console.log(NS, 'customer changes deteted', getChangesAsText(customerChanges));
      return true;
    }

    // Compare booking links
    if (
      _activeBooking.bookingLinks &&
      activeBooking.bookingLinks.length !== _activeBooking.bookingLinks.length
    ) {
      console.log(NS, "bookingLinks length didn't match");
      return true;
    }

    if (
      !CompareObjectsService.hasIdenticalData(
        activeBooking.bookingLinks,
        _activeBooking.bookingLinks
      )
    ) {
      console.log(NS, "bookingLinks contents didn't match");
      return true;
    }

    return false;
  }
);

const cancelConfirmSlice = createSlice({
  name: 'cancelConfirm', // used in action types
  initialState,
  reducers: {
    reset: () => {
      return initialState;
    },
    setIsCancelConfirmStateLoading: (state: ICancelConfirmState, action) => {
      state.isCancelConfirmStateLoading = action.payload;
    },
    setIsAutoRemoveOverlay: (state: ICancelConfirmState, action) => {
      state.isAutoRemoveOverlay = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setCancelConfirm.fulfilled, (state, action) => {
      if(!state.isCancelConfirmStateSet) {
        state.activeBookingToCompare = action.payload.activeBooking;
        state.customerToCompare = action.payload.customer;
        state.isCancelConfirmStateSet = true;
      }
    });
  },
});

// Extract the action creators object and the reducer
const { actions, reducer } = cancelConfirmSlice;
// Extract and export each action creator by name
export { actions as cancelConfirmActions };
// Export the reducer, either as a default or named export
export default reducer;
